import React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Button from "components/Button";
import Shadow from "components/Shadow";
import ImgIcon from "components/imgIcon";
import Separator from "components/Separator";

import { AnimatedCircularProgress } from "react-native-circular-progress";

const Progress: React.FC<{ color: string; fill?: number; counts?: number }> = (
  props
) => {
  return (
    <AnimatedCircularProgress
      padding={2}
      rotation={0}
      size={60}
      width={6}
      lineCap="round"
      fill={props.counts ? 0 : props.fill}
      tintColor={props.color}
      backgroundColor={colors.progressBarInactive}
      childrenContainerStyle={{
        backgroundColor: props.counts
          ? colors.progressBarInactive
          : colors.white,
      }}
      children={() => (
        <Text
          weight="extraBold"
          color={props.counts ? props.color : colors.text.normal}
        >
          {props.counts > 0
            ? props.counts
            : props?.fill > 0
            ? props?.fill?.toFixed(0) + "%"
            : "0%"}
        </Text>
      )}
    />
  );
};

import { sizes, colors } from "assets/constants";
import tinycolor from "tinycolor2";
import { alertPermission, sn } from "utils";
import { icons } from "assets/icons";

const NormalVersion: React.FC<PrimaryItemCardT> = (props) => {
  return (
    <Shadow style={styles.normalV.container}>
      <View style={{ flex: 1, height: 100 }}>
        <Shadow
          shadow={{ opacity: 1, radius: 1 }}
          style={{
            borderRadius: sizes.radius.lg,
            alignItems: "center",
            backgroundColor: colors.white,
            flexDirection: "row-reverse",
            width: "100%",
            height: "100%",
            paddingHorizontal: sizes.space,
          }}
        >
          <View style={styles.normalV.iconContainer}>
            <ImgIcon
              color={tinycolor(props.color).darken(10).toString()}
              {...props?.imgIcon}
            />
          </View>

          <View style={styles.normalV.title}>
            <Text size={"sm"} weight="extraBold">
              {props?.title}
            </Text>
            <Text size={"xs"} weight="bold">
              {props?.countsDescription}
            </Text>
          </View>
          <View style={{ flex: 1 }} />
          <View>
            {props.total !== null || props.total !== undefined ? (
              <Progress
                color={props?.color}
                fill={Number(props?.counts / props?.total) * 100}
              />
            ) : (
              <Progress color={props?.color} counts={props?.counts} />
            )}
          </View>
        </Shadow>
      </View>

      {/* add button section */}
      <TouchableOpacity
        onPress={props.disabledAdd ? alertPermission : props.onAdd}
        style={styles.normalV.add}
        activeOpacity={sizes.tapOpacity}
      >
        <Icon type="ion" name={"add"} color={props?.color} size={32} />
      </TouchableOpacity>
    </Shadow>
  );
};

const LargeVersion: React.FC<PrimaryItemCardT> = (props) => {
  const { color, counts, countsDescription, title, onAdd, imgIcon, total } =
    props;

  return (
    <Shadow style={styles.largeV.container}>
      <View style={styles.largeV.leftContent}>
        {/* progress section */}
        <View>
          {total !== null || total !== undefined ? (
            <Progress color={color} fill={Number(counts / total) * 100} />
          ) : (
            <Progress color={color} counts={counts} />
          )}
        </View>
        <Text
          top={10}
          color={tinycolor(color).darken(25).toString()}
          align="center"
          size={"xs"}
          weight="extraBold"
        >
          {countsDescription}
        </Text>
      </View>

      <View style={styles.largeV.rightContent}>
        <View style={styles.largeV.iconContainer}>
          <ImgIcon
            color={tinycolor(color).darken(10).toString()}
            size={35}
            {...imgIcon}
          />
        </View>
        <Text align="center" weight="extraBold">
          {title}
        </Text>
        <Button
          onPress={props.disabledAdd ? alertPermission : onAdd}
          style={{ marginTop: 10 }}
          square={45}
          bgColor={"transparent"}
          bordered={{
            width: 1,
            color: tinycolor(color).lighten(50).toString(),
          }}
          icon={{
            name: "plus",
            color: tinycolor(color).darken(20).toString(),
            size: 23,
          }}
        />
      </View>
    </Shadow>
  );
};

const PrimaryItemCard: React.FC<PrimaryItemCardT> = (props) => {
  return (
    <TouchableOpacity
      onPress={props.disabled ? alertPermission : props.onPress}
      activeOpacity={props?.disabled ? 1 : sizes.tapOpacity}
    >
      {props?.disabled && (
        <View
          style={{
            position: "absolute",
            top: -5,
            right: -5,
            zIndex: 1000,
          }}
        >
          <ImgIcon icon={icons._no_entry} size={22} />
        </View>
      )}
      <View style={{ opacity: props.disabled ? 0.5 : 1 }}>
        {sn(
          NormalVersion(props),
          LargeVersion(props),
          LargeVersion(props),
          LargeVersion(props)
        )}
      </View>
    </TouchableOpacity>
  );
};

export default PrimaryItemCard;

const styles = {
  normalV: StyleSheet.create({
    container: {
      height: 100,
      flexDirection: "row-reverse",
      borderRadius: sizes.radius.xl,
      backgroundColor: "#ECEFF1",
      borderWidth: 1,
      borderColor: colors.white,
    },

    content: {
      flex: 1,
      alignItems: "center",
      padding: sizes.space,
      flexDirection: "row-reverse",
      justifyContent: "space-between",
      borderRadius: sizes.radius.xl,
      zIndex: 1000,
      backgroundColor: colors.white,
      height: "100%",
    },

    iconContainer: {
      width: 55,
      height: 55,
      borderRadius: 55,
      alignItems: "center",
      justifyContent: "center",
    },

    title: { marginHorizontal: sizes.space, width: 125 },

    add: {
      width: 50,
      height: "100%",
      alignItems: "center",
      justifyContent: "center",
      // borderWidth: 1,
    },
  }),

  largeV: StyleSheet.create({
    container: {
      height: 150,
      flexDirection: "row",
      backgroundColor: colors.white,
      borderRadius: sizes.radius.lg,
    },

    leftContent: {
      flex: 2,
      padding: 10,
      alignItems: "center",
      justifyContent: "center",
    },

    rightContent: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: 10,
    },

    iconContainer: {
      width: 45,
      height: 45,
      borderRadius: 45,
      alignItems: "center",
      justifyContent: "center",
    },
  }),
};
